import { readPref, writePref } from '../prefs';

/**
 * **Vitesse de combat en debug** (v0.49.1, A99) : un facteur `×1 / ×2 / ×4 / ×8`
 * appliqué au **temps de jeu** pendant les Expéditions et les Invasions, et une
 * case « Sans vol de dés » (les dés se posent aussitôt). Outil de test : le
 * déroulé du combat est identique à ×1 (mêmes ennemis, mêmes lancers aux
 * mêmes instants de jeu), seul le temps réel est divisé.
 *
 * Deux préférences de l'**installation** (`localStorage`, comme le reste de
 * `ui/prefs.ts`) — jamais dans la sauvegarde — relues au lancement **seulement
 * en mode debug** (règle du projet, `debugEnabledByChannel()` : développement,
 * canal préprod ou `?debug`) : `main.ts` appelle `enableSpeed(true)`, qui les lit, sans quoi le module reste à
 * ×1 et vol normal, même si les clés existent, et les réglages sont refusés.
 *
 * La vue de combat lit `currentSpeed()` / `noFlight()` à chaque image ; le
 * panneau de debug, la pastille du HUD et `main.ts` (marquage de la partie)
 * s'abonnent avec `onSpeedChange`.
 */

export type Speed = 1 | 2 | 4 | 8;

/** Facteurs proposés, dans l'ordre d'affichage. */
export const SPEEDS: readonly Speed[] = [1, 2, 4, 8];

export const SPEED_KEY = 'diceordie.debug.speed';
export const NO_FLIGHT_KEY = 'diceordie.debug.noFlight';

/** Toute valeur hors de {1, 2, 4, 8} (absente, texte, 3, 16, NaN…) devient 1. */
export function parseSpeed(raw: unknown): Speed {
  const n = typeof raw === 'string' ? Number(raw.trim()) : raw;
  return n === 2 || n === 4 || n === 8 ? n : 1;
}

let enabled = false;
let speed: Speed = 1;
let noFlightOn = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of [...listeners]) fn();
}

/**
 * Active (ou non) les réglages de vitesse. En mode debug, relit les
 * préférences de l'installation ; hors debug, remet ×1 et vol normal sans
 * toucher aux clés. À appeler une fois au lancement (`main.ts`).
 */
export function enableSpeed(debug: boolean): void {
  enabled = debug;
  speed = debug ? parseSpeed(readPref(SPEED_KEY)) : 1;
  noFlightOn = debug && readPref(NO_FLIGHT_KEY) === '1';
  notify();
}

/** Facteur de vitesse courant (1 hors debug). */
export function currentSpeed(): Speed {
  return enabled ? speed : 1;
}

/** « Sans vol de dés » est-il actif (faux hors debug) ? */
export function noFlight(): boolean {
  return enabled && noFlightOn;
}

/** Pose le facteur (valeur refusée → 1), le persiste, prévient les abonnés ; renvoie la valeur effective. */
export function setSpeed(value: unknown): Speed {
  if (!enabled) return 1;
  const next = parseSpeed(value);
  speed = next;
  writePref(SPEED_KEY, String(next));
  notify();
  return next;
}

/** Règle « Sans vol de dés », le persiste, prévient les abonnés ; renvoie la valeur effective. */
export function setNoFlight(on: boolean): boolean {
  if (!enabled) return false;
  noFlightOn = on === true;
  writePref(NO_FLIGHT_KEY, noFlightOn ? '1' : '0');
  notify();
  return noFlightOn;
}

/** Un réglage s'écarte-t-il du jeu normal (la partie doit être marquée, la pastille s'affiche) ? */
export function speedActive(): boolean {
  return currentSpeed() !== 1 || noFlight();
}

/** Texte de la pastille du HUD (« ×4 », « ×4 · sans vol », « sans vol »), `null` si rien n'est actif. */
export function speedBadgeText(): string | null {
  const f = currentSpeed();
  const nf = noFlight();
  if (f === 1 && !nf) return null;
  if (f === 1) return 'sans vol';
  return nf ? `×${f} · sans vol` : `×${f}`;
}

/** S'abonne aux changements (facteur, case, activation) ; renvoie le désabonnement. */
export function onSpeedChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
